import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { checkLogin, apiError } from "../../store/actions";
import UserStore from "../../store/UserStore";
import { toast } from "react-toastify";
import { AdminLogin } from "../../Endpoint/Admin";
import { setUser } from "../../config/TokenHelper";
import mainLogo from "../../assets/images/logo-light.png";

/**
 * Staff console sign-in, rebuilt to the redesign handoff (screen A1 · 01).
 *
 * Split canvas: a photographic left half carrying the console's pitch, and a
 * 420px form on the cream right half.
 *
 * The authentication logic is unchanged from the previous screen — same
 * AdminLogin call, same token handling, same redirect. Note the API takes the
 * email in the `mobile` field: the route validates only that key and the
 * controller resolves either an email or a phone number from it.
 *
 * The design also shows a BRANCH selector on this screen. It is deliberately
 * NOT built: branch scope is not chosen at sign-in here — a branch admin is
 * pinned to their branch by the server and cannot override it, and a global
 * admin picks a branch from the rail afterwards. Offering a choice at login
 * that the server may ignore would be a lie in the UI.
 */

const C = {
  brown: "#3d2817",
  brownDeep: "#2b2018",
  taupe: "#7b6552",
  taupeLight: "#a3917c",
  slate: "#5c5148",
  canvas: "#f7ecdc",
  fieldBorder: "#e6d8c4",
};

// Hero image from the prototype. Kept as the design specifies; swap for an
// asset of the actual property when there is one.
const HERO =
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1400&q=80&auto=format&fit=crop";

const STATS = [
  { value: "4", label: "branches live" },
  { value: "312", label: "rooms managed" },
  { value: "24/7", label: "order desk" },
];

class Login extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mobile: "",
      password: "",
      showPassword: false,
      remember: true,
      submitting: false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    this.props.apiError("");
  }

  togglePasswordVisibility = () => {
    this.setState((s) => ({ showPassword: !s.showPassword }));
  };

  handleSubmit(e) {
    if (e) e.preventDefault();
    const { mobile, password } = this.state;
    if (!mobile.trim() || !password) {
      toast.error("Enter your email and password");
      return;
    }

    this.setState({ submitting: true });
    AdminLogin({ mobile: mobile.trim(), password })
      .then((response) => {
        if (!response || response.status !== 200) {
          toast.error((response && response.message) || "Sign in failed");
          this.setState({ submitting: false });
          return;
        }
        const authToken = response.authToken;
        const authKey = response.authKey;
        UserStore.set(
          () => ({
            token: authToken,
            authkey: authKey,
            user: JSON.stringify(response.data),
          }),
          "login"
        );
        setUser(authToken, authKey, JSON.stringify(response.data));
        window.location.href = "/dashboard";
      })
      .catch(() => {
        toast.error("Could not reach the server");
        this.setState({ submitting: false });
      });
  }

  field({ label, icon, children }) {
    return (
      <div>
        <div
          style={{
            fontSize: 13, fontWeight: 500, color: C.taupe,
            letterSpacing: ".4px", marginBottom: 8,
          }}
        >
          {label}
        </div>
        <div
          style={{
            display: "flex", alignItems: "center", gap: 12, height: 56,
            padding: "0 18px", background: "#fff",
            border: `1px solid ${C.fieldBorder}`, borderRadius: 14,
          }}
        >
          <span className="ms" style={{ fontSize: 21, color: C.taupeLight }}>{icon}</span>
          {children}
        </div>
      </div>
    );
  }

  render() {
    const { showPassword, remember, submitting } = this.state;
    const input = {
      flex: 1, border: 0, outline: "none", background: "transparent",
      fontSize: 16, color: C.brownDeep, fontFamily: "inherit",
    };

    return (
      <div
        style={{
          display: "grid", gridTemplateColumns: "1fr 1fr",
          // An implicit grid row is content-sized, so min-height alone left the
          // photo panel short with cream showing beneath it. Pin the row to the
          // viewport so both halves are full-bleed.
          gridTemplateRows: "1fr", height: "100vh",
          background: C.canvas,
          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
        }}
      >
        {/* Left — the pitch */}
        <div style={{ position: "relative", overflow: "hidden" }}>
          <img
            src={HERO}
            alt=""
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div
            style={{
              position: "absolute", inset: 0,
              background:
                "linear-gradient(160deg, rgba(61,40,23,.86) 0%, rgba(61,40,23,.52) 48%, rgba(61,40,23,.8) 100%)",
            }}
          />
          <div
            style={{
              position: "absolute", inset: 0, padding: 56, display: "flex",
              flexDirection: "column", justifyContent: "space-between",
            }}
          >
            <img
              src={mainLogo}
              alt="Hotel Lubumbashi"
              style={{ height: 58, width: "auto", objectFit: "contain" }}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "1.6px", color: "rgba(255,255,255,.62)" }}>
                STAFF CONSOLE
              </div>
              <div
                style={{
                  marginTop: 18, fontSize: 46, fontWeight: 300, lineHeight: 1.14,
                  letterSpacing: "-1px", color: "#fff", maxWidth: 480,
                }}
              >
                Every room, table, order and ride — one console.
              </div>
              <div style={{ marginTop: 40, display: "flex", gap: 40 }}>
                {STATS.map((s) => (
                  <div key={s.label}>
                    <div style={{ fontSize: 30, fontWeight: 500, color: "#fff" }}>{s.value}</div>
                    <div style={{ marginTop: 4, fontSize: 14, color: "rgba(255,255,255,.6)" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right — the form */}
        <div style={{ display: "grid", placeItems: "center", padding: 56, overflowY: "auto" }}>
          <form onSubmit={this.handleSubmit} style={{ width: 420 }}>
            <div style={{ fontSize: 34, fontWeight: 300, color: C.brownDeep, letterSpacing: "-.6px" }}>
              Welcome back
            </div>
            <div style={{ marginTop: 10, fontSize: 16, color: C.taupe }}>
              Sign in to the Lubumbashi operations console.
            </div>

            <div style={{ marginTop: 36, display: "flex", flexDirection: "column", gap: 18 }}>
              {this.field({
                label: "EMAIL",
                icon: "mail",
                children: (
                  <input
                    type="text"
                    autoComplete="username"
                    value={this.state.mobile}
                    onChange={(e) => this.setState({ mobile: e.target.value })}
                    placeholder="you@hotellubumbashi.cd"
                    style={input}
                  />
                ),
              })}
              {this.field({
                label: "PASSWORD",
                icon: "lock",
                children: (
                  <React.Fragment>
                    <input
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      value={this.state.password}
                      onChange={(e) => this.setState({ password: e.target.value })}
                      placeholder="••••••••"
                      style={input}
                    />
                    <span
                      className="ms"
                      onClick={this.togglePasswordVisibility}
                      style={{ fontSize: 21, color: C.taupeLight, cursor: "pointer" }}
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? "visibility_off" : "visibility"}
                    </span>
                  </React.Fragment>
                ),
              })}
            </div>

            <div
              style={{
                marginTop: 20, display: "flex", alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                onClick={() => this.setState({ remember: !remember })}
                style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, color: C.slate, cursor: "pointer" }}
              >
                <span
                  style={{
                    width: 20, height: 20, borderRadius: 6,
                    background: remember ? C.brown : "transparent",
                    border: remember ? "none" : `1px solid ${C.fieldBorder}`,
                    display: "grid", placeItems: "center",
                  }}
                >
                  {remember && <span className="ms" style={{ fontSize: 15, color: "#fff" }}>check</span>}
                </span>
                Keep me signed in
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                marginTop: 30, width: "100%", height: 58, border: 0, borderRadius: 29,
                background: C.brown, color: "#fff", fontFamily: "inherit", fontSize: 17,
                letterSpacing: ".4px", cursor: submitting ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                boxShadow: "0 10px 24px rgba(61,40,23,.24)", opacity: submitting ? 0.75 : 1,
              }}
            >
              {submitting ? "Signing in…" : "Sign in"}
              {!submitting && <span className="ms" style={{ fontSize: 20 }}>arrow_forward</span>}
            </button>
          </form>
        </div>
      </div>
    );
  }
}

const mapStatetoProps = (state) => {
  const { loading, error } = state.Login;
  return { loading, error };
};

export default withRouter(connect(mapStatetoProps, { checkLogin, apiError })(Login));
